import * as React from "react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import CheckIcon from "@images/icons/checkmark-heavy.svg";

const clamps: any = {
  0: "h-[0px] overflow-hidden",
  1: "line-clamp-1 h-[calc(1em*1.5*1)]",
  2: "line-clamp-2 h-[calc(1em*1.5*2)]",
  3: "line-clamp-3 h-[calc(1em*1.5*3)]",
  4: "line-clamp-4 h-[calc(1em*1.5*4)]",
  5: "line-clamp-5 h-[calc(1em*1.5*5)]",
  6: "line-clamp-6 h-[calc(1em*1.5*6)]",
  7: "line-clamp-7 h-[calc(1em*1.5*7)]",
  8: "line-clamp-8 h-[calc(1em*1.5*8)]",
};

const leadings: any = {
  1: "leading-[100%]",
  1.2: "leading-[120%]",
  1.3: "leading-[133%]",
  1.5: "leading-[150%]",
  1.7: "leading-[170%]",
  2: "leading-[200%]",
};
const weights: any = {
  normal: "font-normal",
  semibold: "font-semibold",
  bold: "font-bold",
};

const aligns: any = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
};

const sizes: any = {
  7: "text-[7px]",
  8: "text-[8px]",
  9: "text-[9px]",
  10: "text-10",
  11: "text-11",
  12: "text-12",
  13: "text-13",
  14: "text-14",
  15: "text-15",
  16: "text-16",
  17: "text-17",
  18: "text-18",
  19: "text-19",
  21: "text-[21px]",
  22: "text-22",
  25: "text-25",
  28: "text-28",
  30: "text-[30px]",
  35: "text-35",
};

const smsizes: any = {
  7: "sm:text-[7px]",
  8: "sm:text-[8px]",
  10: "sm:text-10",
  11: "sm:text-11",
  12: "sm:text-12",
  13: "sm:text-13",
  14: "sm:text-14",
  15: "sm:text-15",
  16: "sm:text-16",
  17: "sm:text-17",
  18: "sm:text-18",
  19: "sm:text-19",
  21: "sm:text-[21px]",
  22: "sm:text-22",
  25: "sm:text-25",
  28: "sm:text-28",
  30: "sm:text-[30px]",
  35: "sm:text-35",
  40: "sm:text-40",
};

const lgsizes: any = {
  7: "lg:text-[7px]",
  8: "lg:text-[8px]",
  10: "lg:text-10",
  11: "lg:text-11",
  12: "lg:text-12",
  13: "lg:text-13",
  14: "lg:text-14",
  15: "lg:text-15",
  16: "lg:text-16",
  17: "lg:text-17",
  18: "lg:text-18",
  19: "lg:text-19",
  21: "lg:text-[21px]",
  22: "lg:text-22",
  25: "lg:text-25",
  28: "lg:text-28",
  30: "lg:text-[30px]",
  35: "lg:text-35",
};

const colors: any = {
  blue: "text-blue",
  blue50: "text-blue/50",
  blue10: "text-blue/10",
  green: "text-green",
  green10: "text-green/10",
  orange: "text-orange",
  orange10: "text-orange/10",
  darkorange: "text-darkorange",
  red: "text-red",
  red10: "text-red/10",
  white: "text-white",
  white90: "text-white/90",
  white80: "text-white/80",
  white70: "text-white/70",
  white60: "text-white/60",
  grey: "text-grey",
  grey2: "text-grey2",
  grey3: "text-grey3",
  grey4: "text-grey4",
  lightgreen: "text-lightgreen",
  darkblue: "text-darkblue",
  black: "text-black",
  black90: "text-black/90",
  black80: "text-black/80",
  black70: "text-black/70",
  black60: "text-black/60",
  black50: "text-black/50",
  black40: "text-black/40",
  black30: "text-black/30",
  black20: "text-black/20",
  black10: "text-black/10",
  black05: "text-black/05",
  bordergrey1: "text-bordergrey1",
  bordergrey2: "text-bordergrey2",
  qualityvgood: "text-qualityvgood",
  qualitygood: "text-qualitygood",
  qualityok: "text-qualityok",
  qualitypoor: "text-qualitypoor",
  qualityvpoor: "text-qualityvpoor",
  alert: "text-alert",
  alertbg: "text-alertbg",
};

interface IProps {
  size: number;
  sizeDesktop?: number;
  sizeTablet?: number;
  element?: string;
  leading?: number;
  align?: string;
  weight?: string;
  color?: string;
  bullet?: boolean;
  bulletType?: string;
  opacity?: string;
  italic?: boolean;
  textShadow?: boolean;
  clampExpand?: boolean;
  children?: React.ReactNode;
  lineClamp?: number;
  dangerouslySetInnerHTML?: { __html: string };
  className?: string;
}

const Text = ({
  children,
  italic = false,
  leading = 1,
  align = "left",
  weight = "normal",
  element = "div",
  bullet = false,
  bulletType = "disc",
  size,
  sizeDesktop,
  sizeTablet,
  textShadow = false,
  color = "black",
  lineClamp,
  clampExpand = false,
  dangerouslySetInnerHTML,
  className,
}: IProps) => {
  const [showClamp, setShowClamp] = React.useState(
    lineClamp !== null ? (lineClamp > -1 ? true : false) : false
  );

  const isHeading =
    element.includes("h1") ||
    element.includes("h2") ||
    element.includes("h3") ||
    element.includes("h4") ||
    element.includes("h5") ||
    element.includes("h6");

  const getSizeClasses = () => {
    if (size && !sizeDesktop && !sizeTablet) {
      return `${sizes[size]} ${smsizes[size]} ${lgsizes[size]}`;
    }
    if (size && sizeDesktop && !sizeTablet) {
      return `${sizes[size]} ${smsizes[size]} ${lgsizes[sizeDesktop]}`;
    }
    if (size && !sizeDesktop && sizeTablet) {
      return `${sizes[size]} ${smsizes[sizeTablet]} ${lgsizes[size]}`;
    }
    if (size && sizeDesktop && sizeTablet) {
      return `${sizes[size]} ${smsizes[sizeTablet]} ${lgsizes[sizeDesktop]}`;
    }
  };

  let colorClasses = colors[color];

  let lineClampStyles = clamps[String(lineClamp)];
  let weightStyles = weights[weight];
  let leadingStyles = leadings[leading];
  let alignStyles = aligns[align];

  let style = `${getSizeClasses()}
  ${colorClasses || ""} ${weightStyles || ""} ${leadingStyles || ""} ${
    alignStyles || ""
  } ${italic ? "italic" : ""} ${className || ""}`;

  let showMore = () => {
    if (lineClamp === undefined) return null;
    if (lineClamp > -1 && clampExpand) {
      return (
        <div
          className={`${style} !text-black cursor-pointer flex items-end -mt-[21px] ${
            showClamp ? (lineClamp === 0 ? "h-[42px]" : "h-[57px]") : "h-[42px]"
          } relative z-10`}
          onClick={() => {
            setShowClamp(!showClamp);
          }}
        >
          <>
            <span className="underline font-semibold">
              {showClamp ? "Read More" : "Read Less"}{" "}
            </span>
            <ChevronDownIcon
              className={`inline h-5 w-5 text-black ${
                !showClamp && "rotate-180"
              }`}
              aria-hidden="true"
            />
          </>
        </div>
      );
    }
  };

  const showBullet = () => {
    if (!bullet) return null;

    let icon =
      bulletType === "disc" ? (
        <>&#x2022;</>
      ) : bulletType === "check" ? (
        <CheckIcon className="w-6 h-5 mb-1 text-green inline" />
      ) : (
        ""
      );

    return (
      <span className={`${bulletType === "disc" ? "ml-2" : ""}`}>{icon} </span>
    );
  };

  if (isHeading) {
    const Heading = `${element}` as keyof JSX.IntrinsicElements;
    return (
      <Heading
        className={`${style} ${
          showClamp && `${lineClampStyles || ""}`
        } mb-0 pb-0`}
        style={
          textShadow ? { textShadow: "0px 2px 4px rgba(0, 0, 0, 0.6)" } : {}
        }
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      >
        <span className={`${showBullet() ? "inline-flex gap-2" : ""}`}>
          {showBullet()}
          {children}
        </span>
      </Heading>
    );
  }

  const Element = `${element || "div"}` as keyof JSX.IntrinsicElements;

  if (lineClamp > -1) {
    return (
      <>
        <Element
          className={`${style} ${showClamp && `${lineClampStyles || ""} mb-0`}`}
          style={
            textShadow ? { textShadow: "0px 2px 4px rgba(0, 0, 0, 0.6)" } : {}
          }
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        >
          {children}
        </Element>
        {element.includes("p") && showMore()}
      </>
    );
  }

  if (bullet) {
    return (
      <Element
        className={`${style}`}
        style={
          textShadow ? { textShadow: "0px 2px 4px rgba(0, 0, 0, 0.6)" } : {}
        }
      >
        <span className={`${showBullet() ? "inline-flex gap-2" : ""}`}>
          {showBullet()}
          {children}
        </span>
      </Element>
    );
  }

  return (
    <Element
      className={`${style}`}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      style={textShadow ? { textShadow: "0px 2px 4px rgba(0, 0, 0, 0.6)" } : {}}
    >
      {children}
    </Element>
  );
};

export default Text;
